import React, { useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, Check, CheckCircle, KeyRound, ShieldCheck, UserPlus } from 'lucide-react';
import { apiGetTeam, apiRegisterClient } from '../../api';
import ModalShell from '../Modal/ModalShell';

export default function RegisterClientModal({ onClose, onSuccess, jwtToken, zohoConnected }) {
  const [form, setForm] = useState({ name: '', email: '', assignedProjectManager: '', accessibleBy: [] });
  const [team, setTeam] = useState([]);
  useEffect(() => { apiGetTeam(jwtToken).then((items) => setTeam(items || [])).catch(() => {}); }, [jwtToken]);
  const toggleAccess = (id) => setForm((current) => ({ ...current, accessibleBy: current.accessibleBy.includes(id) ? current.accessibleBy.filter((value) => value !== id) : [...current.accessibleBy, id] }));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await apiRegisterClient(jwtToken, form);
      setResult(data);
      await onSuccess?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <ModalShell
        title="Client registered"
        subtitle="The portal account is ready to use."
        eyebrow="Registration complete"
        icon={CheckCircle}
        tone="success"
        size="sm"
        onClose={onClose}
      >
        <div className="popup-credential-card">
          <span className="popup-credential-icon"><KeyRound size={18} /></span>
          <div>
            <span>Temporary password</span>
            <code>{result.generatedPassword}</code>
            <small>Share this securely with the client.</small>
          </div>
        </div>
        {result.welcomeEmailSent ? (
          <div className="popup-notice popup-notice-success"><CheckCircle size={16} /><span>Welcome email sent successfully via Zoho.</span></div>
        ) : (
          <div className="popup-notice popup-notice-danger">
            <AlertTriangle size={16} />
            <span>Welcome email was not sent. {result.welcomeEmailError || 'Zoho is not connected.'} Please share the password manually.</span>
          </div>
        )}
        <div className="modal-footer popup-footer-single">
          <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title="Register new client"
      subtitle="Create portal access and assign the person responsible for this account."
      eyebrow="Client access"
      icon={UserPlus}
      onClose={onClose}
    >
      <form className="popup-form" onSubmit={submit}>
        <div className="popup-field">
          <label className="field-label">Client name</label>
          <input className="input" placeholder="Jane Smith" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} required />
        </div>
        <fieldset className="client-access-picker">
          <legend><ShieldCheck size={15} />Who can access this client?</legend>
          <p>Admins always retain access. Select the project managers and production users who should see this workspace.</p>
          <div>{team.filter((member) => member.role !== 'admin').map((member) => { const id = String(member.id || member._id); const checked = form.accessibleBy.includes(id); return <button type="button" key={id} className={checked ? 'selected' : ''} onClick={() => toggleAccess(id)}><span>{checked && <Check size={13} />}</span><strong>{member.name}</strong><small>{String(member.role).replace('_', ' ')}</small></button>; })}</div>
        </fieldset>
        <div className="popup-field">
          <label className="field-label">Client email</label>
          <input className="input" type="email" placeholder="jane@example.com" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} required />
        </div>
        <div className="popup-field">
          <label className="field-label">Assigned project manager</label>
          <input className="input" placeholder="Your name" value={form.assignedProjectManager} onChange={(event) => setForm((current) => ({ ...current, assignedProjectManager: event.target.value }))} />
          <small className="popup-field-help">This name appears in the client workspace.</small>
        </div>
        {!zohoConnected && (
          <div className="popup-notice popup-notice-warning">
            <AlertCircle size={16} /><span>Zoho is not connected, so the welcome email will not be sent automatically.</span>
          </div>
        )}
        {error && <div className="popup-error"><AlertCircle size={15} /><span>{error}</span></div>}
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="spinner" /> : 'Register client'}</button>
        </div>
      </form>
    </ModalShell>
  );
}
